"use strict";

module.exports = {

    /**
     * Holds the last ping request object
     * 
     * @var        {Object} 
     */
    pingRequest: null,

    /**
     * Determines the days to set to the cookie
     * 
     * @var        {integer} 
     */
    COOKIE_DAYS: 7,

    /**
     * Holds the items 
     * 
     * @var        {Array} 
     */
    items: [],
    
    /**
     * Holds the groupedItems 
     * 
     * @var        {Array} 
     */
    groupedItems: [],

    /**
     * Holds current local key
     * 
     * @var        {string} 
     */
    locale: "bg",

    /**
     * Holds current version of the cart
     * 
     * Increment the value, if current state of the cart 
     * is not suitable for next versions of the class.
     * 
     * @var        {integer} 
     */
    version: 1,

    /**
     * Holds listener references
     * 
     * @var        {Object} 
     */
    listeners: {},

    /**
     * Holds delivery options
     * 
     * @var        {Object} 
     */
    delivery: {
        price: void(0),
        freeShippingThreshold: void(0)
    },

    acceptTOS: false,

    paymentMethod: null,

    /**
     * Returns whether the items are empty
     *
     * @return     {integer} 
     */
    empty: function() {
        return !this.count();
    },

    /**
     * Group cart items by product_group_id
     */
    groupItems: function() {
        this.groupedItems = this.items.reduce((acc, cur) => {
            const groupId = cur.product_group_id;
            if (!acc[groupId]) {
                acc[groupId] = [];
            }
            acc[groupId].push(cur);
            return acc;
        }, {});
    },

    /**
     * Sets the accept tos.
     *
     * @param      {boolean}  value   The value
     */
    setAcceptTos: function(value) {
        if (this.acceptTOS != value) {
            this.acceptTOS = value;
            this.fire("change");
        }
    },

    /**
     * Returns the count of the items
     *
     * @return     {integer} 
     */
    count: function() {
        return this.items.length;
    },

    /**
     * Adds an item to the list
     *
     * @param      {Object}  item    The item
     */
    add: function(item) {
        this.items.push(item);
        this.groupItems();

        this.fire("add", [ item ]);
        this.save();
    },

    /**
     * Clears the items
     *
     * @param      {boolean}  save    Whether to save the clear action
     */
    clear: function(save = false) {
        let items = this.items.slice(0);

        this.items.splice(0, this.items.length);
        this.groupItems();

        if (save) {
            this.save();
        }

        this.fire("clear", [ items ]);
    },

    /**
     * Removes an item by given index
     *
     * @param      {integer}  index   The index
     */
    remove: function(index) {

        let item = this.items[index];

        this.items.splice(index, 1);
        this.groupItems();

        this.fire("remove", [ item ]);
        this.save();
    },

    /**
     * Removes an item by given product group id
     *
     * @param      {string}  groupId   The product group id
     */
    removeGroup: function(groupId) {
        let itemsToRemove = this.groupedItems[groupId];

        this.items = this.items.filter(item => item.product_group_id != groupId);
        this.groupItems();

        this.fire("removeGroup", [ itemsToRemove ]);
        this.save();
    },

    /**
     * Saves the cart state in the cookie, localStorage and pings the backend
     */
    save: function() {
        let obj = {
            version: this.version,
            items: this.items
        };

        window.Utils.setCookie(this.getStorageKey(), this.serialize(), this.COOKIE_DAYS);
        localStorage.setItem(this.getStorageKey(), JSON.stringify(obj));

        let updateValue = new Date().getTime() + Math.random();
        localStorage.setItem("UPDATE_CART", "" + updateValue);

        this.ping();
    },

    /**
     * Pings the Backend to save the cookies
     */
    ping: function() {
        if (this.pingRequest) {
            this.pingRequest.abort();
        }

        let pingUrl = document.body.dataset["pingUrl"];

        this.pingRequest = $.ajax({
            cache: false,
            type: "GET",
            url: pingUrl,
            success: function(data) {
                // Do nothing
            }
        });
    },

    /**
     * Serializes the items data to save in backend
     *
     * @return     {string}  the serialized string (JSON-encoded)
     */
    serialize: function() {

        let result = [];

        for (const group in this.groupedItems) {
            if (Object.hasOwnProperty.call(this.groupedItems, group)) {
                const groupItems = this.groupedItems[group];
                const item = groupItems[0];
                let options = [];

                for (let selectedOption in item.selectedOptions) {
                    options.push(item.selectedOptions[selectedOption].id);
                }
                
                result.push({
                    id              : item.id,
                    product_group_id: item.product_group_id,
                    quantity        : groupItems.length,
                    options         : options
                });
            }
        }

        return JSON.stringify(result);
    },

    /**
     * Gets the storage key.
     *
     * @return     {string}
     */
    getStorageKey: function() {
        return "cart_" + this.locale;
    },

    /**
     * Loads the items from localStorage.
     */
    loadFromStorage: function() {
        let cartJSON = localStorage.getItem(this.getStorageKey());
        cartJSON = JSON.parse(cartJSON ? cartJSON : "{}");

        if (this.version == cartJSON.version) 
        {
            this.items = cartJSON["items"];
        }
        else {
            this.items = [];
        }

        this.groupItems();

        this.fire("change");
    },

    /**
     * Items setter
     *
     * @param      {Array}  items   The items
     */
    setItems: function(items) {
        this.items = items;
        this.fire("change");
    },

    /**
     * Sets the delivery.
     *
     * @param      {Object}  delivery  The delivery
     */
    setDelivery: function(delivery) {
        this.delivery = delivery;
    },

    /**
     * Inits the class
     */
    init: function() {
        this.locale = document.body.dataset["locale"];

        // Fire change on add remove and clear events
        this.on([ "add", "remove", "removeGroup", "clear" ], function() {
            this.fire("change");
        }.bind(this));

        if (document.body.dataset["cartClear"]) {
            this.clear(true);
        }
        else {

            // If cart cookie exists, reload data from storage
            if (window.Utils.getCookie(this.getStorageKey())) {
                let data = JSON.parse(document.body.dataset["cartItems"] || "[]");
                this.items = data;
                this.groupItems();
                this.fire("change");
            }
            else {
                this.fire("change");
                this.save();
            }

        }

        $(window).on("storage", (e) => {
            e = e.originalEvent;

            if (e.key === null) {
                this.clear();
                return;
            }

            // Shitty IE do not fires the event if value is too long
            if (e.key !== "UPDATE_CART") {
                return;
            }

            // If shitty IE, then postpone the event
            if (
                navigator.appName == 'Microsoft Internet Explorer' ||  
                !!(navigator.userAgent.match(/Trident/) || navigator.userAgent.match(/rv:11/)) || 
                (typeof $.browser !== "undefined" && $.browser.msie == 1)
            ) {
                setTimeout(() => {
                    this.loadFromStorage()
                }, 300);
            }
            else {
                this.loadFromStorage();
            }
        });
    },

    /**
     * Adds listener for given event or events
     *
     * @param      {string|Array}  event     The event or array of events
     * @param      {Function}      callback  The callback
     */
    on: function(event, callback) {
        let events;

        if (typeof event == "string") {
            events = [ event ];
        }
        else {
            events = event;
        }

        for (let i = 0; i < events.length; i++) {
            let event = events[i];

            if (typeof this.listeners[event] == "undefined") {
                this.listeners[event] = [];
            }

            this.listeners[event].push(callback);
        }
    },

    /**
     * Removes listener for given event
     *
     * @param      {string}    event     The event
     * @param      {Function}  callback  The callback
     */
    off: function(event, callback) {

        if (typeof this.listeners[event] == "undefined") {
            return;
        }

        if (this.listeners[event].indexOf(callback) > -1 ){
            this.listeners[event].splice(this.listeners[event].indexOf(callback), 1);
        }
    },

    /**
     * Fires an event with given data 
     *
     * @param      {string}  event   The event
     * @param      {mixed}  data    The data
     */
    fire: function(event, data) {

        if (typeof this.listeners[event] == "undefined") {
            return;
        }

        for (let i = 0, len = this.listeners[event].length; i < len; i++) {
            this.listeners[event][i].apply(this, data);
        }
    }
   
}
